import { Controller } from "@hotwired/stimulus";

/**
 * Stimulus controller for managing the question form, answer options, and data fields.
 * Handles form validation, dirty state, dynamic field addition/removal, and TinyMCE integration.
 *
 * @class QuestionsController
 * @extends Controller
 */


export default class extends Controller {
  /**
   * Tracks dirty state and form submission state.
   * @type {{formDirty: Boolean, formSubmitting: Boolean}}
   */
  static values = { formDirty: Boolean, formSubmitting: Boolean };


  /**
   * Called when the controller is connected to the DOM.
   * Sets up form validation, dirty state tracking, and modal cleanup.
   */
  connect() {
    // this.tinyInit()

    this.dirtyFormConfirmation = this.dirtyFormConfirmation.bind(this);
    document.addEventListener("turbo:before-visit", this.dirtyFormConfirmation);

    // Attach change handlers to all form fields to track unsaved changes
    $("#questionForm, #answer_option_form, #data_field_form").each(function (index, value) {
      let self = this;
      $(value).find('input, select').each(function () {
        $(value).change(function () {
          self.unsavedChanges();
        });
      });
      $(value).on('submit', function () {
        self.setFormSubmitting();
      });

      // Set up jQuery validation with custom error handling
      $(value).validate({
        errorPlacement: function (error, element) {
          return true;
        },
        highlight: function (element, errorClass, validator) {
          $(element).addClass(errorClass);
          $(element).closest("tr").addClass(errorClass);
          self.setFormSubmitting(false);
        },
        unhighlight: function (element, errorClass, validator) {
          $(element).removeClass(errorClass);
          $(element).closest("tr").removeClass(errorClass);
          self.setFormSubmitting(false);
        },
        showErrors: function (errorMap, errorList) {
          var errors = this.numberOfInvalids();
          var message = '';
          if (errors) {
            if (errors == 1) {
              message = 'You missed 1 field. It has been highlighted';
            } else {
              message = 'You have missed ' + errors + ' fields. Please fill before submitted.';
            }
            $(".question__error").html(message);
            $(".question__error").show();
          } else {
            $(".question__error").hide();
          }
          // ensures that highlight/unhighlight will function
          this.defaultShowErrors();
        },
        submitHandler: function () {
          self.setFormSubmitting();
          this.currentForm.submit();
        }
      });
    }.bind(this));

    // Clear modal input fields when modals are closed
    $('#editMultipleAOModal').on('hidden.bs.modal', function () {
      $('#edit_mult_inputAO')[0].value = "";
    });

    $('#editMultipleDFModal').on('hidden.bs.modal', function () {
      $('#edit_mult_inputDF')[0].value = "";
    });
  }


  /**
   * Called when the controller is disconnected from the DOM.
   * Cleans up event listeners and TinyMCE instances.
   */
  disconnect() {
    document.removeEventListener('turbo:before-visit', this.dirtyFormConfirmation);
    tinymce.remove("textarea#question-text");
  }


  /**
   * Shows a validation error message for the question form.
   * @param {number} errors - Number of errors found
   */
  questionValidatorMessage(errors) {
    $("#errorExplanation").hide();
    var message = '';
    if (errors) {
      if (errors == 1) {
        message = 'You missed 1 field. It has been highlighted';
      } else {
        message = 'You missed ' + errors + ' fields. They have been highlighted';
      }
      $(".question__error").html(message);
      $(".question__error").show();
    } else {
      $(".question__error").hide();
    }
  }


  /**
   * Marks the form as dirty and shows the unsaved changes warning.
   */
  unsavedChanges() {
    $('#unsaved-changes').show();
    this.formDirtyValue = true;
  }


  /**
   * Sets the form submitting state.
   * @param {boolean} submitting
   */
  setFormSubmitting(submitting) {
    if (submitting == false) {
      this.formSubmitting = false;
    } else {
      this.formSubmitting = true;
    }
  }


  /**
   * Shows a confirmation dialog if the user tries to leave with unsaved changes.
   * @param {Event} event
   */
  dirtyFormConfirmation(event) {
    if (this.formSubmittingValue || !this.formDirtyValue) {
      return undefined;
    }
    var confirmationMessage = 'You have unsaved changes. '
      + 'If you leave before saving, your changes will be lost.';
    if (!confirm(confirmationMessage)) {
      event.preventDefault();
    }
  }


  /**
   * Handles file uploads for TinyMCE image fields.
   * @param {Object} blobInfo
   * @param {Function} success
   * @param {Function} failure
   * @param {Function} progress
   */
  handleFileUpload(blobInfo, success, failure, progress) {
    var xhr, formData;
    xhr = new XMLHttpRequest();
    xhr.withCredentials = false;
    var token = document.querySelector("meta[name=csrf-token]").content;
    xhr.open("POST", "/uploads");
    xhr.setRequestHeader("X-CSRF-Token", token);
    xhr.upload.onprogress = function (e) {
      progress(e.loaded / e.total * 100);
    };
    xhr.onload = function () {
      var json;
      if (xhr.status < 200 || xhr.status >= 300) {
        failure("HTTP Error: " + xhr.status);
        return;
      }
      json = JSON.parse(xhr.responseText);
      if (!json || typeof json.location != "string") {
        failure("Invalid JSON: " + xhr.responseText);
        return;
      }
      success(json.location);
    };
    xhr.onerror = function () {
      failure("Image upload failed due to a XHR Transport error. Code: " + xhr.status);
    };
    formData = new FormData();
    formData.append("file", blobInfo.blob(), blobInfo.filename());
    xhr.send(formData);
  }


  /**
   * Initializes TinyMCE editor for question text fields.
   * Handles image uploads and dirty state tracking.
   */
  tinyInit() {
    var self = this;
    tinymce.init({
      verify_html: false,
      cleanup: false,
      height: 300,
      selector: "textarea#question-text",
      plugins: "advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table",
      relative_urls: false,
      remove_script_host: false,
      convert_urls: true,
      toolbar: "insertfile undo redo | styleselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image | code | fullscreen",
      promotion: false,
      valid_children: "+body[style]",
      images_upload_url: "/uploads",
      image_dimensions: false,
      theme: 'silver',
      image_class_list: [
        { title: "Inline Question Image Style", value: "question-img" }
      ],
      automatic_uploads: true,
      images_upload_handler: (blobInfo, success, failure) => {
        this.handleFileUpload(blobInfo, success, failure, (progress) => {
          console.log(`Upload progress: ${progress}%`);
        });
      },
      setup(editor) {
        editor.on('blur', function () {
          if (editor.isDirty()) {
            self.unsavedChanges();
          }
        });
      }
    });
  }


  /**
   * Finds the next available precode for answer options or data fields.
   * @param {string} className - jQuery selector for precode inputs
   * @returns {number}
   */
  findNextPrecode(className) {
    let existingCodes = [];
    $(className).each(function () {
      if ($(this).is(':visible')) {
        var precode = parseInt($(this).attr('data-precode'));
        existingCodes.push(precode);
      }
    });
    // Always include 0 to avoid NaN issues
    existingCodes.push(0);
    const filteredCodes = existingCodes.filter(code => $.isNumeric(code));
    const nextIndex = Math.max.apply(Math, filteredCodes) + 1;
    return nextIndex;
  }


  /**
   * Edits multiple answer options or data fields at once from modal input.
   * Handles both label and precode assignment, and dynamically adds new fields as needed.
   * @remarks
   * The sleep(1) call is a workaround for a race condition in dynamic field addition.
   */
  editMultiple() {
    const type = event.params.fieldtype;
    const id = type === 'answer_option' ? 'edit_mult_inputAO' : 'edit_mult_inputDF';
    const className = type === 'answer_option' ? 'answer_options_attributes' : 'data_fields_attributes';
    const buttonName = type === 'answer_option' ? ".add_ans_fields" : ".add_dat_fields";
    const inputText = document.getElementById(id).value;
    const labels = inputText.split("\n");
    const formId = type === 'answer_option' ? 'answer_option_form' : 'data_field_form';
    const form = document.getElementById(formId);

    for (let i = 0; i < labels.length; i++) {
      let split = labels[i].split("\t");
      const precode = split.length < 2 ? this.findNextPrecode(`input[name^='question[${className}]'][name*=precode]`) : split[1];
      const existing_field = $(`input[name^='question[${className}]'][name*='[precode]'][data-precode='${precode}']`).eq(0);

      // This sleep is a workaround for a race condition in dynamic field addition
      this.sleep(1);

      if (existing_field.length > 0) {
        $(`input[name^='question[${className}]'][name*='[label]'][data-id='${existing_field.data('id')}']`).val(split[0]);
      } else {
        let time = new Date().getTime();
        let regexp = new RegExp($(buttonName).data('id'), 'g');
        if (type == 'answer_option') {
          $('#answer_option_table tr:last').after($(buttonName).data('fields').replace(regexp, time));
        } else {
          $('#data_field_table tr:last').after($(buttonName).data('fields').replace(regexp, time));
        }
        $(buttonName).attr("prev_time", time);
        event.preventDefault();

        const pretime = $(buttonName).attr("prev_time");
        const label_input = $(`#question_${className}_${pretime}_label`);
        label_input.val(split[0]);
        label_input.attr('data-id', pretime);

        const precode_input = $(`#question_${className}_${pretime}_precode`);
        precode_input.val(precode);
        precode_input.attr('data-id', pretime);
        precode_input.attr('data-precode', precode);
      }
    }

    $(".close-reveal-modal").click();
  }


  /**
   * Busy-wait sleep for the given number of milliseconds.
   * Used as a workaround for timing issues in editMultiple.
   * @param {number} milliseconds
   */
  sleep(milliseconds) {
    const date = Date.now();
    let currentDate = null;
    do {
      currentDate = Date.now();
    } while (currentDate - date < milliseconds);
  }


  /**
   * Adds a new answer option or data field with the next available precode.
   * @remarks
   * Relies on findNextPrecode and dynamic field addition logic.
   */
  addFieldWithPrecode() {
    const type = event.params.fieldtype;
    const className = type === 'answer_option' ? 'answer_options_attributes' : 'data_fields_attributes';
    const buttonName = type === 'answer_option' ? ".add_ans_fields" : ".add_dat_fields";
    const nextIndex = this.findNextPrecode(`input[name^='question[${className}]'][name*=precode]`);

    $(buttonName).click();
    const pretime = $(buttonName).attr("prev_time");
    const precode_input = $(`#question_${className}_${pretime}_precode`);
    precode_input.val(nextIndex);
    precode_input.attr('data-id', pretime);
    precode_input.attr('data-precode', nextIndex);

    this.unsavedChanges();
  }


  /**
   * Deletes a data field row and clears its values.
   * @param {Event} event
   * @param {Element} datafield
   */
  deleteDataField(event, datafield) {
    var existing_label, existing_precode, id;
    let target = datafield || event.currentTarget;
    $(target).prev('input[type=hidden]').val('true');
    id = $(target).prev('input[type=hidden]').attr('data-id');
    existing_precode = $("input[name^='question[data_fields_attributes]'][name*='[precode]'][data-id='" + id + "']").eq(0);
    existing_precode.attr('data-id', "");
    existing_precode.attr('data-precode', "");
    existing_precode.val("");
    existing_label = $("input[name^='question[data_fields_attributes]'][name*='[label]'][data-id='" + id + "']").eq(0);
    existing_label.attr('data-id', "");
    $(target).closest('tr').hide();
    event.preventDefault();
    this.unsavedChanges();
  }


  /**
   * Deletes all selected data field rows.
   */
  deleteSelectedDataFields() {
    $("input[name='ids[]']:checked").each(function (index, value) {
      let link_ref = $(value).closest('tr').find('a.remove_fields');
      this.deleteDataField(event, link_ref);
    }.bind(this));
    $('#select-all-ids').prop("checked", false);
  }


  /**
   * Deletes an answer option row and clears its values.
   * @param {Event} event
   * @param {Element} datafield
   * @remarks
   * TODO: Merge this into deleteField and deleteSelectedFields for DRYness.
   */
  deleteAnswerOption(event, datafield) {
    var existing_label, existing_precode, id;
    let target = datafield || event.currentTarget;
    $(target).prev('input[type=hidden]').val('true');
    id = $(target).prev('input[type=hidden]').attr('data-id');
    existing_precode = $("input[name^='question[answer_options_attributes]'][name*='[precode]'][data-id='" + id + "']").eq(0);
    existing_precode.attr('data-id', "");
    existing_precode.attr('data-precode', "");
    existing_precode.val("");
    existing_label = $("input[name^='question[answer_options_attributes]'][name*='[label]'][data-id='" + id + "']").eq(0);
    existing_label.attr('data-id', "");
    $(target).closest('tr').hide();
    event.preventDefault();
    this.unsavedChanges();
  }

  /**
   * Deletes all selected answer option rows.
   */
  deleteSelectedAnswerOptions() {
    $("input[name='answer_ids[]']:checked").each(function (index, value) {
      let link_ref = $(value).closest('tr').find('a.remove_fields');
      this.deleteAnswerOption(event, link_ref);
    }.bind(this));
    $('#select-all-ids').prop("checked", false);
  }
}
